import React, { useState, useEffect, useCallback } from 'react'
import { useParams } from 'react-router-dom';
import { useContext } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import { useTeams } from '../../contexts/TeamsContext';
import { Button } from '../../components/ui/button';
import { Download, Key, RefreshCw } from 'lucide-react';
import { CredentialsApi, PublicLeak } from 'api-client';
import { getApiConfiguration } from '../../api/utils';
import { formatTimestamp } from '@/utils/dateTime';
import AssetNotes from '@/components/assets/detail/AssetNotes';
import { Badge } from '@/components/ui/Badge';

function PublicLeakDetail() {
    const { id } = useParams<{ id: string }>();
    const { currentTeam } = useTeams();
    const { token } = useContext(AuthContext);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [leak, setLeak] = useState<PublicLeak | null>(null);
    const fetchLeak = useCallback(async () => {
        if (!currentTeam?.slug || !token || !id) return;

        setLoading(true);
        setError(null);

        try {
            const api = new CredentialsApi(getApiConfiguration(token));
            const response = await api.publicLeaksRetrieve({
                id: id,
                teamSlug: currentTeam.slug,
            });
            setLeak(response);
        } catch (err) {
            console.error('Error fetching public leak:', err);
            setError('Failed to load public leak');
        } finally {
            setLoading(false);
        }
    }, [currentTeam?.slug, token, id]);

    useEffect(() => {
        fetchLeak();
    }, [fetchLeak]);
    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
                <div className="flex items-center justify-between">
                    <div className="flex items-start space-x-4">
                        <div className="p-3 rounded-lg bg-purple-500/10">
                            <Key className="h-6 w-6 text-purple-500" />
                        </div>
                        <div>
                            <div className="flex items-center space-x-3">
                                <h1 className="text-2xl font-bold text-white">Public Leak Details</h1>
                                <Badge
                                    className={'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'}
                                >
                                    Active
                                </Badge>
                            </div>
                            <div className="flex items-center space-x-4 mt-2">
                                {/* <span className="text-sm text-gray-400">Service: {leak?.source}</span> */}
                                <span className="text-sm text-gray-400">Email: {leak?.identifier}</span>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center space-x-3">
                        <Button
                            variant="outline"
                            onClick={() => {
                                // TODO: Implement export functionality
                                console.log('Export clicked');
                            }}
                        >
                            <Download className="h-4 w-4 mr-2" />
                            Export
                        </Button>
                        <Button
                            onClick={() => {
                                // TODO: Implement refresh functionality
                                console.log('Refresh clicked');
                            }}
                        >
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Refresh
                        </Button>
                    </div>
                </div>
            </div>
            {loading || error ? (
                <>
                    {loading && (
                        <div className="flex items-center justify-center h-12">
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-400"></div>
                        </div>
                    )}
                    {error && (
                        <div className="text-red-500">{error}</div>
                    )}
                </>
            ) : (
                <>
                    <div className="space-y-4">
                        {/* Main Content - 2 columns */}
                        <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
                            <h2 className="text-lg font-semibold text-white mb-4">Leak Information</h2>
                            <div className="grid grid-cols-2 gap-6">
                                <div className="space-y-4">
                                    <div>
                                        <div className="text-sm text-gray-400">Service</div>
                                        <div className="text-sm text-white mt-1">{leak?.source}</div>
                                    </div>

                                    <div>
                                        <div className="text-sm text-gray-400">Source Name</div>
                                        <div className="text-sm text-white mt-1">{leak?.sourceName}</div>
                                    </div>
                                </div>
                                {leak?.password && (
                                    <div className="space-y-4">
                                        <div>
                                            <div className="text-sm text-gray-400">Password</div>
                                            <div className="text-sm text-white mt-1">{leak?.password}</div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <AssetNotes objectId={id || ""} objectType="publicleak" />
                </>
            )}
        </div>
    );
}

export default PublicLeakDetail